import React, {useState} from "react";
import styles from "./Report.module.css";
import Content from "../../content-interface";
import Principles from "../principles/Principles";
import DOMPurify from "dompurify";
import ToggleSwitch from "../toggleSwitch/ToggleSwitch";

const Report = (props: {
  principles: Content['principles'],
  principleCategories: Content['principleCategories'],
  questions: Content['questions'],
  answers: { [key: number]: boolean },
  fullFxDownloadLink?: string,
  setCurrStep: React.Dispatch<number>
}) => {
  const date = new Date();
  const {principles, principleCategories, questions, answers} = props;
  const [showCopiedText, setShowCopiedText] = useState(false);
  const [selectedPrinciple, setSelectedPrinciple] = useState<string | undefined>(undefined);

  const getPrinciplesRowCount = (): number => {
    return Object.keys(principleCategories || {}).reduce((count, principleCategoryId) => {
      const principleCategoryIdNumber = parseInt(principleCategoryId, 10);
      return Math.max(Object.values(principles || {}).filter(principle => principle.principleCategory === principleCategoryIdNumber).length, count);
    }, 0);
  }

  const isPrincipleConflicted = (principleId: number): boolean => {
    return questions
      .some((question, i) => {
        if (answers[i + 1] === undefined)
          return false;

        if (typeof question.conflictingPrinciples === 'string' ?
          question.conflictingPrinciples !== 'all' :
          !question.conflictingPrinciples.includes(principleId))
          return false; // We don't care for this question

        return !answers[i + 1];
      });
  }

  const renderPrincipleTd = (row: number, principleCategoryId: number) => {
    const principleId = Object.keys(principles)
      .filter(principleId => principles[principleId]?.principleCategory === principleCategoryId)
      .slice(row, row + 1)
      .pop();
    const isConflicted = principleId ? isPrincipleConflicted(parseInt(principleId, 10)) : undefined;

    return (
      <td onClick={() => principleId !== undefined && setSelectedPrinciple(principleId)}
          className={principleId && isConflicted !== undefined ? (isConflicted ? styles.red : styles.green) : ''}
          key={`${row}${principleCategoryId}`}>
        {principleId}
      </td>
    );
  }

  const share = () => {
    window?.navigator?.clipboard?.writeText(window.location.href);
    setShowCopiedText(true);
    setTimeout(() => setShowCopiedText(false), 3000);
  }

  const print = () => {
    window?.print();
  }

  return (
    <div className='Report'>
      <h4>Report</h4>
      <p>
        Based on your responses, the following (highlighted in green) Governing Principles from the FX Global Code apply to you as a Market Participant.
      </p>
      <p>
        The report has been generated on
        {` ${date.toLocaleDateString('en', {weekday: 'long'})}, ${date.toLocaleDateString('en', {day: '2-digit'})}`}.
        {` ${date.toLocaleDateString('en', {month: 'short'})} ${date.getFullYear()} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`}.
        {props.fullFxDownloadLink && <span> The full FX Global Code can be downloaded <a href={props.fullFxDownloadLink}>here</a>.</span>}
      </p>

      <table className={styles.table}>
        <thead className={styles.header}>
        <tr>
          <th colSpan={Object.keys(principleCategories).length}>Governing Principles</th>
        </tr>
        <tr>
          {Object.values(principleCategories).map(principleCategory => <th key={principleCategory.title}>{principleCategory.title}</th>)}
        </tr>
        </thead>
        <tbody className={styles.body}>
        {[...Array(getPrinciplesRowCount())].map((_, i) => <tr key={i}>
          {Object.keys(principleCategories).map(principleCategoryId => renderPrincipleTd(i, parseInt(principleCategoryId, 10)))}
        </tr>)}
        </tbody>
      </table>

      <div className='navButtons'>
        <button className={showCopiedText ? 'light' : ''} onClick={share}>
          <i>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M18 9C19.6569 9 21 7.65685 21 6C21 4.34315 19.6569 3 18 3C16.3431 3 15 4.34315 15 6C15 6.12549 15.0077 6.24919 15.0227 6.37063L8.08261 9.84066C7.54305 9.32015 6.80891 9 6 9C4.34315 9 3 10.3431 3 12C3 13.6569 4.34315 15 6 15C6.80891 15 7.54305 14.6798 8.08261 14.1593L15.0227 17.6294C15.0077 17.7508 15 17.8745 15 18C15 19.6569 16.3431 21 18 21C19.6569 21 21 19.6569 21 18C21 16.3431 19.6569 15 18 15C17.1911 15 16.457 15.3202 15.9174 15.8407L8.97733 12.3706C8.99229 12.2492 9 12.1255 9 12C9 11.8745 8.99229 11.7508 8.97733 11.6294L15.9174 8.15934C16.457 8.67985 17.1911 9 18 9Z"
                fill="currentColor"/>
            </svg>
          </i>
          {showCopiedText ? 'Link has been saved to Clipboard' : 'Share Report'}
        </button>
        <button onClick={print}>
          <i>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 18H17V16H7V18Z" fill="currentColor"/>
              <path d="M17 14H7V12H17V14Z" fill="currentColor"/>
              <path d="M7 10H11V8H7V10Z" fill="currentColor"/>
              <path fillRule="evenodd" clipRule="evenodd"
                    d="M6 2C4.34315 2 3 3.34315 3 5V19C3 20.6569 4.34315 22 6 22H18C19.6569 22 21 20.6569 21 19V9C21 5.13401 17.866 2 14 2H6ZM6 4H13V9H19V19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V5C5 4.44772 5.44772 4 6 4ZM15 4.10002C16.6113 4.4271 17.9413 5.52906 18.584 7H15V4.10002Z"
                    fill="currentColor"/>
            </svg>
          </i>
          Print Report
        </button>
      </div>

      <h4>Questions Overview</h4>
      <div className={styles.allQuestionsContainer}>
        {questions.map((question, i) => <div key={i} className={styles.questionContainer}>
          <div className={styles.questionContent}>
            <button onClick={() => props.setCurrStep(i+1)}>Question {i+1}</button>
            <span>: </span>
            <span dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(question.questionHtmlContent)}} />
          </div>
          <ToggleSwitch value={answers[i + 1] === undefined ? true : answers[i + 1]} disabled={true} className={styles.toggleSwitch} id='answer'/>
        </div>)}
      </div>

      <h4>Governing Principles</h4>
      <Principles openPrincipleId={selectedPrinciple}
                  principles={Object.keys(principles)
                    .filter(principleId => !isPrincipleConflicted(parseInt(principleId, 10)))
                    .reduce((acc, principleId) => ({...acc, [principleId]: principles[principleId]}), {})}/>
    </div>
  );
}

export default Report;
